/**
 * API helper functions for lesson plan chat feature
 */

// Use the same pattern as other API calls in the codebase
// The Django main urls.py includes kumu urls at /api/ directly
const API_BASE_URL = '/api';

/**
 * Get auth token from localStorage
 */
const getAuthToken = () => {
  return localStorage.getItem('authToken');
};

/**
 * Send a chat message and get AI response
 * @param {string} lessonPlanId - ID of the lesson plan
 * @param {string} message - User's message
 * @param {string|null} selectedSection - Currently selected section (optional)
 * @param {Array} chatHistory - Previous messages in the chat
 * @returns {Promise<Object>} Response object with AI message and proposed changes
 */
export const sendChatMessage = async (lessonPlanId, message, selectedSection = null, chatHistory = []) => {
  const token = getAuthToken();
  
  if (!token) {
    throw new Error('No authentication token found');
  }

  const response = await fetch(`${API_BASE_URL}/lesson-plan-chat/`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Token ${token}`
    },
    body: JSON.stringify({
      lesson_plan_id: lessonPlanId,
      message: message,
      selected_section: selectedSection,
      chat_history: chatHistory
    })
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
  }

  return await response.json();
};

/**
 * Apply approved changes to a lesson plan section
 * @param {string} lessonPlanId - ID of the lesson plan
 * @param {string} sectionName - Name of the section to update
 * @param {string} newContent - New content for the section
 * @param {string} action - Action type: 'update', 'add', 'remove', 'rename', 'move'
 * @param {string} newName - New section name (for rename action)
 * @param {string} position - Position for add/move actions: 'top', 'bottom', 'after:SectionName', 'before:SectionName'
 * @returns {Promise<Object>} Response with updated lesson plan
 */
export const applyChanges = async (lessonPlanId, sectionName, newContent, action = 'update', newName = null, position = null) => {
  const token = getAuthToken();
  
  if (!token) {
    throw new Error('No authentication token found');
  }

  const response = await fetch(`${API_BASE_URL}/lesson-plan-chat/apply-changes/`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Token ${token}`
    },
      body: JSON.stringify({
        lesson_plan_id: lessonPlanId,
        section_name: sectionName,
        new_content: newContent,
        action: action,
        new_name: newName,
        position: position
      })
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
  }

  return await response.json();
};

/**
 * Calculate diff statistics between old and new content
 * @param {string} oldContent - Original content
 * @param {string} newContent - New content
 * @returns {Object} Object with linesAdded and linesRemoved counts
 */
export const calculateDiffStats = (oldContent, newContent) => {
  const oldLines = oldContent.split('\n');
  const newLines = newContent.split('\n');
  
  // Simple diff calculation - count new and removed lines
  const linesAdded = Math.max(0, newLines.length - oldLines.length);
  const linesRemoved = Math.max(0, oldLines.length - newLines.length);
  
  return { linesAdded, linesRemoved };
};

/**
 * Extract section content from full lesson plan markdown
 * @param {string} lessonPlan - Full lesson plan markdown
 * @param {string} sectionName - Name of the section to extract
 * @returns {string|null} Section content or null if not found
 */
export const extractSectionContent = (lessonPlan, sectionName) => {
  if (!lessonPlan || !sectionName) return null;
  
  // Match section heading and content until next heading or end
  const pattern = new RegExp(`## ${sectionName}\\s*\\n(.*?)(?=\\n## |$)`, 's');
  const match = lessonPlan.match(pattern);
  
  return match ? match[1].trim() : null;
};

